import { useRef, useState } from "react"

import { useMsal } from "@azure/msal-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { MemoryItem } from "@/api/memory"

import { getToken } from "@/api/auth-api"
import {
  addMemoryApi,
  approveMemoryCandidateApi,
  deleteMemoryApi,
  getMemoriesApi,
  triggerMaintenanceApi,
  updateMemoryApi,
} from "@/api/memory"
import { Check, Edit, Plus, Save, Trash2, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { MAX_ADD_TO_MEMORY_TEXT_LENGTH } from "@/features/chat/components/system-message-utils"
import { usePrismMemory } from "@/hooks/use-prism-memory"

import { PrismConversationsSection } from "./prism-conversations-section"

export type MemoryPanelInlineProps = {
  active: boolean
}

export function MemoryPanelInline({ active }: MemoryPanelInlineProps) {
  const prismMemory = usePrismMemory()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { instance } = useMsal()

  const [activeTab, setActiveTab] = useState<"stm" | "ltm">("stm")
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState("")
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [newMemoryText, setNewMemoryText] = useState("")
  const [newMemoryType, setNewMemoryType] = useState<"stm" | "ltm">("stm")

  const { data: memories, isLoading } = useQuery({
    queryKey: ["memories"],
    queryFn: async () => {
      const tokenResult = await getToken(instance)
      return getMemoriesApi(tokenResult?.accessToken)
    },
    enabled: active && !prismMemory,
  })

  // Drives the "new memories to review" tab indicator off the same status
  // field the accept action already uses — no new state to track.
  const hasUnreviewedMemories = (memories?.stm ?? []).some((item) => item.status === "suggested")

  // Set synchronously on the first click so a double click can't start a
  // second save before `isPending` re-renders the button disabled (#3322 B6).
  const addInFlightRef = useRef(false)

  const addMutation = useMutation({
    mutationFn: async (variables: { type: "stm" | "ltm"; text: string }) => {
      const tokenResult = await getToken(instance)
      return addMemoryApi(variables.type, { text: variables.text }, tokenResult?.accessToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      setIsAddDialogOpen(false)
      setNewMemoryText("")
      toast.success(t("personalization.memoryPanel.toasts.added"))
    },
    onError: () => {
      toast.error(t("personalization.memoryPanel.toasts.saveFailed"))
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (variables: { id: string; type: "stm" | "ltm"; text: string }) => {
      const tokenResult = await getToken(instance)
      return updateMemoryApi({
        memoryId: variables.id,
        memoryType: variables.type,
        updates: { text: variables.text },
        idToken: tokenResult?.accessToken,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      setEditingId(null)
      setEditText("")
      toast.success(t("personalization.memoryPanel.toasts.updated"))
    },
    onError: () => {
      toast.error(t("personalization.memoryPanel.toasts.saveFailed"))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (variables: { id: string; type: "stm" | "ltm" }) => {
      const tokenResult = await getToken(instance)
      return deleteMemoryApi(variables.id, variables.type, tokenResult?.accessToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      toast.success(t("personalization.memoryPanel.toasts.deleted"))
    },
    onError: () => {
      toast.error(t("personalization.memoryPanel.toasts.deleteFailed"))
    },
  })

  const approveMutation = useMutation({
    mutationFn: async (variables: { text: string }) => {
      const tokenResult = await getToken(instance)
      return approveMemoryCandidateApi(variables.text, tokenResult?.accessToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      toast.success(t("personalization.memoryPanel.toasts.saved"))
    },
    onError: () => {
      toast.error(t("personalization.memoryPanel.toasts.saveFailed"))
    },
  })

  const maintenanceMutation = useMutation({
    mutationFn: async () => {
      const tokenResult = await getToken(instance)
      return triggerMaintenanceApi(tokenResult?.accessToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      toast.success(t("personalization.memoryPanel.toasts.maintenanceTriggered"))
    },
  })

  const handleEditStart = (item: MemoryItem) => {
    setEditingId(item.id)
    setEditText(item.text)
  }

  const handleEditSave = (item: MemoryItem, type: "stm" | "ltm") => {
    updateMutation.mutate({ id: item.id, type, text: editText })
  }

  const handleAdd = () => {
    if (addInFlightRef.current) return
    addInFlightRef.current = true
    addMutation.mutate(
      { type: newMemoryType, text: newMemoryText },
      {
        onSettled: () => {
          addInFlightRef.current = false
        },
      }
    )
  }

  if (prismMemory) return <PrismConversationsSection active={active} />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Button size="sm" onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> {t("personalization.memoryPanel.actions.addMemory")}
        </Button>
        <Button
          disabled={maintenanceMutation.isPending}
          size="sm"
          variant="ghost"
          onClick={() => maintenanceMutation.mutate()}
        >
          {maintenanceMutation.isPending
            ? t("personalization.memoryPanel.actions.running")
            : t("personalization.memoryPanel.actions.runMaintenance")}
        </Button>
      </div>

      <Tabs
        className="flex flex-col"
        value={activeTab}
        onValueChange={(v: string) => setActiveTab(v as "stm" | "ltm")}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger className="gap-1.5" value="stm">
            {t("personalization.memoryPanel.tabs.stm")}
            {hasUnreviewedMemories && (
              <>
                <span aria-hidden="true" className="bg-primary inline-block size-2 rounded-full" />
                <span className="sr-only">{t("personalization.memoryPanel.tabs.unreviewed")}</span>
              </>
            )}
          </TabsTrigger>
          <TabsTrigger value="ltm">{t("personalization.memoryPanel.tabs.ltm")}</TabsTrigger>
        </TabsList>

        <TabsContent value="stm">
          {isLoading ? (
            <div>{t("common.loading")}</div>
          ) : (
            <div className="mt-4 space-y-4">
              {memories?.stm.map((item) => (
                <Card key={item.id}>
                  <CardContent className="p-4">
                    {editingId === item.id ? (
                      <div className="space-y-2">
                        <Textarea
                          maxLength={MAX_ADD_TO_MEMORY_TEXT_LENGTH}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                        />
                        <div className="flex justify-end gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                aria-label={t("personalization.memoryPanel.actions.cancelEdit")}
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingId(null)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("personalization.memoryPanel.actions.cancelEdit")}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                aria-label={t("personalization.memoryPanel.actions.saveEdit")}
                                size="sm"
                                onClick={() => handleEditSave(item, "stm")}
                              >
                                <Save className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("personalization.memoryPanel.actions.saveEdit")}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <p className="min-w-0 text-sm break-all">{item.text}</p>
                        <div className="flex shrink-0 items-center gap-1">
                          {item.status === "suggested" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Radix tooltip-on-disabled-control wrapper must stay focusable */}
                                <span tabIndex={approveMutation.isPending ? 0 : -1}>
                                  <Button
                                    className="size-8"
                                    disabled={approveMutation.isPending}
                                    size="icon"
                                    variant="ghost"
                                    aria-label={t(
                                      "personalization.memoryPanel.actions.acceptSuggested"
                                    )}
                                    onClick={() => approveMutation.mutate({ text: item.text })}
                                  >
                                    <Check className="text-success h-4 w-4" />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t("personalization.memoryPanel.actions.acceptSuggested")}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                aria-label={t("personalization.memoryPanel.actions.editMemory")}
                                className="size-8"
                                size="icon"
                                variant="ghost"
                                onClick={() => handleEditStart(item)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("personalization.memoryPanel.actions.editMemory")}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                aria-label={t("personalization.memoryPanel.actions.deleteMemory")}
                                className="text-destructive size-8"
                                size="icon"
                                variant="ghost"
                                onClick={() => deleteMutation.mutate({ id: item.id, type: "stm" })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("personalization.memoryPanel.actions.deleteMemory")}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    )}
                    <div className="text-muted-foreground mt-2 flex gap-2 text-xs">
                      <span>{new Date(item.created_at).toLocaleDateString()}</span>
                      {item.status && (
                        <Badge className="h-4 text-[10px]" variant="secondary">
                          {item.status}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {memories?.stm.length === 0 && (
                <div className="text-muted-foreground py-8 text-center">
                  {t("personalization.memoryPanel.empty.stm")}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="ltm">
          {isLoading ? (
            <div>{t("common.loading")}</div>
          ) : (
            <div className="mt-4 space-y-4">
              {memories?.ltm.map((item) => (
                <Card key={item.id}>
                  <CardContent className="p-4">
                    {editingId === item.id ? (
                      <div className="space-y-2">
                        <Textarea
                          maxLength={MAX_ADD_TO_MEMORY_TEXT_LENGTH}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                        />
                        <div className="flex justify-end gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                aria-label={t("personalization.memoryPanel.actions.cancelEdit")}
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingId(null)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("personalization.memoryPanel.actions.cancelEdit")}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                aria-label={t("personalization.memoryPanel.actions.saveEdit")}
                                size="sm"
                                onClick={() => handleEditSave(item, "ltm")}
                              >
                                <Save className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("personalization.memoryPanel.actions.saveEdit")}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <p className="min-w-0 text-sm break-all">{item.text}</p>
                        <div className="flex shrink-0 items-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                aria-label={t("personalization.memoryPanel.actions.editMemory")}
                                className="size-8"
                                size="icon"
                                variant="ghost"
                                onClick={() => handleEditStart(item)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("personalization.memoryPanel.actions.editMemory")}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                aria-label={t("personalization.memoryPanel.actions.deleteMemory")}
                                className="text-destructive size-8"
                                size="icon"
                                variant="ghost"
                                onClick={() => deleteMutation.mutate({ id: item.id, type: "ltm" })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("personalization.memoryPanel.actions.deleteMemory")}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    )}
                    <div className="text-muted-foreground mt-2 flex gap-2 text-xs">
                      {item.type && (
                        <Badge className="h-4 text-[10px]" variant="outline">
                          {item.type}
                        </Badge>
                      )}
                      {item.last_updated_at && (
                        <span>
                          {t("personalization.memoryPanel.updatedAt", {
                            date: new Date(item.last_updated_at).toLocaleDateString(),
                          })}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {memories?.ltm.length === 0 && (
                <div className="text-muted-foreground py-8 text-center">
                  {t("personalization.memoryPanel.empty.ltm")}
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <PrismConversationsSection active={active} />

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("personalization.memoryPanel.dialog.title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">
                {t("personalization.memoryPanel.dialog.type")}
              </legend>
              <div className="flex gap-4">
                <Button
                  variant={newMemoryType === "stm" ? "default" : "outline"}
                  onClick={() => setNewMemoryType("stm")}
                >
                  {t("personalization.memoryPanel.dialog.shortTerm")}
                </Button>
                <Button
                  variant={newMemoryType === "ltm" ? "default" : "outline"}
                  onClick={() => setNewMemoryType("ltm")}
                >
                  {t("personalization.memoryPanel.dialog.longTerm")}
                </Button>
              </div>
            </fieldset>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="new-memory-content">
                {t("personalization.memoryPanel.dialog.content")}
              </label>
              <Textarea
                id="new-memory-content"
                maxLength={MAX_ADD_TO_MEMORY_TEXT_LENGTH}
                placeholder={t("personalization.memoryPanel.dialog.placeholder")}
                value={newMemoryText}
                onChange={(e) => setNewMemoryText(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button disabled={!newMemoryText.trim() || addMutation.isPending} onClick={handleAdd}>
              {t("personalization.memoryPanel.dialog.add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
